import {
  InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import {
  api,
  ExternalUserPartialListDto,
  PlainExternalUserDto,
  UpdateExternalUserDto,
} from '@sib/api';
import { DEFAULT_PAGE } from '@sib/api/constants';
import { QueryKey } from '@sib/api/enums';

const fetchCoworkers = async (
  params: Parameters<
    typeof api.externalUsers.externalUsersControllerFindAllCoworkers
  >[0],
  pageParam: number = params?.page || DEFAULT_PAGE
) =>
  api.externalUsers.externalUsersControllerFindAllCoworkers({
    ...params,
    filter: params?.filter?.filter(i => !i.includes('organizationId')),
    page: pageParam as number,
  });

export const useGetCoworkers = (
  params: Parameters<
    typeof api.externalUsers.externalUsersControllerFindAllCoworkers
  >[0],
  options?: Partial<UseQueryOptions<ExternalUserPartialListDto>>
) =>
  useQuery({
    ...options,
    queryKey: [QueryKey.Resource.ExternalUsers, params],
    queryFn: () => fetchCoworkers(params),
  });

export enum GetInfiniteUsersType {
  Coworkers = 'coworkers',
  OrganizationUsers = 'organization-users',
}

export const useGetInfiniteUsers = (
  type: GetInfiniteUsersType,
  params: Parameters<
    typeof api.externalUsers.externalUsersControllerFindAllCoworkers
  >[0],
  organizationId?: string,
  options?: Partial<
    UseInfiniteQueryOptions<
      ExternalUserPartialListDto,
      Error,
      InfiniteData<ExternalUserPartialListDto>
    >
  >
) =>
  useInfiniteQuery({
    ...options,
    queryKey: [
      QueryKey.Resource.ExternalUsers,
      QueryKey.Operation.List,
      organizationId ?? QueryKey.Resource.OrganizationCoworkers,
      params,
      QueryKey.Operation.Infinite,
    ],
    enabled:
      type === GetInfiniteUsersType.OrganizationUsers ? !!organizationId : true,
    queryFn: async ({ pageParam }) => {
      if (type === GetInfiniteUsersType.OrganizationUsers)
        return api.organization.organizationControllerFindAllUsers(
          organizationId || '',
          {
            ...params,
            page: pageParam as number,
          }
        );

      if (type === GetInfiniteUsersType.Coworkers)
        return fetchCoworkers(params, pageParam as number);

      throw new Error('Invalid type');
    },
    getNextPageParam: lastPage => {
      const { hasNextPage } = lastPage;
      const nextPage = lastPage.page + 1;

      return hasNextPage ? nextPage : undefined;
    },
    placeholderData: keepPreviousData,
    initialPageParam: params?.page || DEFAULT_PAGE,
  });

interface UpdateExternalUserParams {
  userId: string;
  data: UpdateExternalUserDto;
}

export const useUpdateExternalUser = (
  options?: UseMutationOptions<
    PlainExternalUserDto,
    Error,
    UpdateExternalUserParams
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: async ({ userId, data }) =>
      api.externalUsers.externalUsersControllerUpdateExternalUser(userId, data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.Resource.User] });
      options?.onSuccess?.(data, variables, context);
    },
  });
};
